import React, { useMemo, useContext, createContext, useCallback } from 'react';
import { HTTPError } from 'clients/HTTPClient';

import useReducerWithMiddleware from '../useReducerWithMiddleware';
import unauthorized from '../unauthorized';
import {
    GET_TICKET_REQUESTED,
    GET_TICKET_FAILED,
    GET_TICKET_SUCCESS,
    WAIVE_TICKET_REQUESTED,
    WAIVE_TICKET_FAILED,
    WAIVE_TICKET_SUCCESS,
} from './constants';
import ticketReducer, { initialTicketState, TicketState } from './reducer';
import { ticketClient } from 'clients';

interface TicketAction {
    getTicket: (identifier: string) => void;
    waiveTicket: (identifier: string) => void;
}

interface TicketContextType extends TicketState, TicketAction {}

export const TicketContext = createContext<TicketContextType | null>(null);

export const TicketProvider: React.FunctionComponent<React.PropsWithChildren<object>> = ({ children }) => {
    const [state, dispatch] = useReducerWithMiddleware(ticketReducer, { ...initialTicketState }, [], [unauthorized]);

    const getTicket = useCallback(
        async (id: string) => {
            dispatch({ type: GET_TICKET_REQUESTED });

            try {
                let tickets = null;
                try {
                    // @TODO: we should let the backend determine by plate vs. by ticket
                    tickets = [await ticketClient.getTicket(id)];
                } catch (error) {
                    const plateTickets = (await ticketClient.getTicketByPlate(id)) as any[];
                    if (plateTickets.length === 0) {
                        throw new Error(`Could not find a violation for ${id}`);
                    }
                    tickets = plateTickets;
                }
                dispatch({ type: GET_TICKET_SUCCESS, payload: { tickets } });
            } catch (error) {
                let errorMessage;
                if (HTTPError.isHTTPError(error)) {
                    errorMessage = (error.cause as { detail: string }).detail;
                } else {
                    errorMessage = error?.toString() || 'Could not find that violation';
                }
                dispatch({
                    type: GET_TICKET_FAILED,
                    payload: { error, message: errorMessage },
                });
            }
        },
        // until this is fixed: https://github.com/reactjs/react.dev/issues/1889,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const waiveTicket = useCallback(
        async (id: string) => {
            dispatch({ type: WAIVE_TICKET_REQUESTED });
            try {
                await ticketClient.waiveTicket(id);
                dispatch({ type: WAIVE_TICKET_SUCCESS });
            } catch (error) {
                dispatch({
                    type: WAIVE_TICKET_FAILED,
                    payload: { message: (error as Error).toString() || 'Could not find ticket' },
                });
            }
        },
        // until this is fixed: https://github.com/reactjs/react.dev/issues/1889,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const value = useMemo<TicketContextType>(() => {
        return {
            ...state,
            getTicket,
            waiveTicket,
        };
    }, [state, getTicket, waiveTicket]);
    return <TicketContext.Provider value={value}>{children}</TicketContext.Provider>;
};

export const useTicket = () => {
    const context = useContext(TicketContext);
    if (!context) {
        throw new Error('Error: useTicket should be wrapped by TicketProvider.');
    }
    return context;
};
